import React, { createContext, useContext, useState, useEffect } from "react";
import { CartType } from "../../models/CartType";
import { CartService } from "../../services/functions/cartService";
import { SaleItem } from "../../models/saleItem";
import { onAuthStateChanged } from "firebase/auth";
import { auth, logEvent } from "../../firebase";
import { useNavigate } from "react-router-dom";
import { CartItemUnavailable, PurchaseCartResponseType } from "../../models/CartServiceType";
import { MetricsNames } from "../../services/firebase/metricsService";
import UnavailableItemsDialog from "../../checkout/errorDialogs/ItemsNotAvailableDialog";
import MultiRestaurantErrorDialog from "../../checkout/errorDialogs/multiRestaurantErrorDialog";

// Define CartContext outside the component
const CartContext = createContext<CartContextType | null>(null);

interface CartContextType {
  cart: CartType | null;
  loading: boolean;
  unavailableItems: CartItemUnavailable[];
  fetchCart: () => Promise<CartType | null>;
  addItem: (quantity: number, restaurantId: string, saleItem: SaleItem) => Promise<void>;
  removeItem: (restaurantId: string, saleItem: SaleItem) => Promise<void>;
  checkout: () => Promise<boolean>
}

interface CartProviderProps {
  children: React.ReactNode;
}

export const CartProvider = ({ children }: CartProviderProps) => {
  const [cart, setCart] = useState<CartType | null>(null);
  const [loading, setLoading] = useState(true);
  const [unavailableItems, setUnavailableItems] = useState<CartItemUnavailable[]>([]);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [multiRestaurantDialogOpen, setMultiRestaurantDialogOpen] = useState(false);
  const navigate = useNavigate();

  const fetchCart = async (): Promise<CartType | null> => {
    let fetchedCart: CartType | null = null
    setLoading(true);
    try {
      fetchedCart = await CartService.fetchCart();
      setCart(fetchedCart);
    } catch (error) {
      console.error("Failed to fetch cart:", error);
    } finally {
      setLoading(false);
    }
    setLoading(false);
    return fetchedCart;
  };

  const addItem = async (quantity: number, restaurantId: string, saleItem: SaleItem) => {
    try {
      const response = await CartService.addItemToCart(quantity, restaurantId, saleItem);
      if (response.cart) {
        setCart(response.cart);  // Refresh cart after updating
      }
      if (response.error && response.error == "Different restaurnt") {
        setMultiRestaurantDialogOpen(true);
      }
    } catch (error) {
      console.error("Failed to add item:", error);
    }
  };

  const removeItem = async (restaurantId: string, saleItem: SaleItem) => {
    try {
      const cart = await CartService.removeItemFromCart(restaurantId, saleItem);
      setCart(cart);  // Refresh cart after updating
    } catch (error) {
      console.error("Failed to remove item:", error);
    }
  };


  const checkout = async (): Promise<boolean> => {
    try {
      const response: PurchaseCartResponseType = await CartService.purchaseCart();

      if (response.error) {
        setErrorDialogOpen(true);
        return false;
      }
      if (response.unavailableItems && response.unavailableItems.length > 0) {
        setUnavailableItems(response.unavailableItems);
        setErrorDialogOpen(true);
        if (response.cart) {
          setCart(response.cart);
        }
      }
      if (response.yocoResponse) {
        logEvent(MetricsNames.ADD_PAYMENT_INFO);
        window.location.href = response.yocoResponse.redirectUrl;
      }
    } catch (error) {
      console.error("Failed to remove item:", error);
    }
    return true
  };


  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      fetchCart()
    });

    return () => unsubscribe(); // Clean up the listener on unmount
  }, [auth]);

  return <CartContext.Provider value={{
    cart,
    loading,
    unavailableItems,
    fetchCart,
    addItem,
    removeItem,
    checkout,
  }}>
    {children}

    <MultiRestaurantErrorDialog
      open={multiRestaurantDialogOpen}
      onClose={() => setMultiRestaurantDialogOpen(false)}
    />

    <UnavailableItemsDialog
      open={errorDialogOpen}
      restaurantId={cart ? cart.restaurantId : ""}
      onClose={() => setErrorDialogOpen(false)}
      unavailableItems={unavailableItems}
    />
  </CartContext.Provider>
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
